import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { EventResponseMetaData, EventsFilter, EventsStats, UserEvents } from "../../entities/event";
import { AnalyticsSummary, getActiveUsersInMonthly, getAnalyticsSummary, getEventsStats, getUserEvents } from "./thunks";

interface EventState {
  loading: boolean;
  loadingActiveUsers: boolean;
  activeUsersLoaded: boolean;
  userEvents: UserEvents[];
  filter: EventsFilter;
  meta: EventResponseMetaData;
  monthlyActiveUsersCount: number;
  stats: EventsStats | null;
  statsLoading: boolean;
  summary: AnalyticsSummary | null;
  summaryLoading: boolean;
}

const initialState: EventState = {
  loading: false,
  loadingActiveUsers: false,
  activeUsersLoaded: false,
  userEvents: [],
  filter: {},
  meta: {
    limit: 50,
    page: 1,
    pages: 1,
    total: 0,
    totalUniqueUsers: 0,
  },
  monthlyActiveUsersCount: 0,
  stats: null,
  statsLoading: false,
  summary: null,
  summaryLoading: false,
};

export const eventSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    handlerFilter: (state: Draft<EventState>, action: PayloadAction<EventsFilter>) => {
      state.filter = action.payload;
    }
  },
  extraReducers: (builder) => {
    // getUserEvents
    builder.addCase(getUserEvents.pending, (state: Draft<EventState>) => {
      state.loading = true;
    })
    builder.addCase(getUserEvents.fulfilled, (state: Draft<EventState>, action) => {
      state.loading = false;
      if (action.payload) {
        const { data, ...meta } = action.payload
        state.userEvents = data;
        const pages = meta.limit > 0 ? Math.max(1, Math.ceil(meta.total / meta.limit)) : 1;
        state.meta = { ...meta, pages };
      }
    })
    builder.addCase(getUserEvents.rejected, (state: Draft<EventState>) => {
      state.loading = false;
    })
    // getEventsStats
    builder.addCase(getEventsStats.pending, (state: Draft<EventState>) => {
      state.statsLoading = true;
    })
    builder.addCase(getEventsStats.fulfilled, (state: Draft<EventState>, action) => {
      state.statsLoading = false;
      if (action.payload) state.stats = action.payload;
    })
    builder.addCase(getEventsStats.rejected, (state: Draft<EventState>) => {
      state.statsLoading = false;
    })
    // getAnalyticsSummary
    builder.addCase(getAnalyticsSummary.pending, (state: Draft<EventState>) => {
      state.summaryLoading = true;
    })
    builder.addCase(getAnalyticsSummary.fulfilled, (state: Draft<EventState>, action) => {
      state.summaryLoading = false;
      if (action.payload) state.summary = action.payload;
    })
    builder.addCase(getAnalyticsSummary.rejected, (state: Draft<EventState>) => {
      state.summaryLoading = false;
    })
    //getActiveUsersInMonthly
    builder.addCase(getActiveUsersInMonthly.pending, (state: Draft<EventState>) => {
      state.loadingActiveUsers = true;
    })
    builder.addCase(getActiveUsersInMonthly.fulfilled, (state: Draft<EventState>, action) => {
      state.loadingActiveUsers = false;
      state.activeUsersLoaded = true;
      state.monthlyActiveUsersCount = action.payload;
    })
    builder.addCase(getActiveUsersInMonthly.rejected, (state: Draft<EventState>) => {
      state.loadingActiveUsers = false;
      state.activeUsersLoaded = true;
    })
  },
});

export const { handlerFilter } = eventSlice.actions;

export const selectEventState = (state: RootState) => state.event;

export default eventSlice.reducer;
